import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/atlassian/pipelines/agent/build/platform/services/design-system-docs/src/__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/templates/page.tsx";
import colorStyles from './_images/color-styles.png';
import componentStyles from './_images/component-styles.png';
import iconographyStyles from './_images/iconography-styles.png';
import jiraScreenNewStyles from './_images/jira-screen-new-styles.png';
import stylesDropdownCustom from './_images/styles-dropdown-custom.png';
import stylesDropdownLegacy from './_images/styles-dropdown-legacy.png';
import typographyStyles from './_images/typography-styles.png';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`At `}<a parentName="p" {...{
        "href": "https://events.atlassian.com/team-digital/"
      }}>{`Team ‘25`}</a>{`, we announced a new brand and design
direction with one goal: to unleash the potential of every team through clear, modern, and
consistent design.`}</p>
    <p>{`Since then, Atlassian websites and apps have been making moves towards this refreshed new style.`}</p>
    <p>{`Learn what’s changing in our design system and how to use the new styles.`}</p>
    <h2 {...{
      "id": "whats-changing"
    }}>{`What’s changing`}</h2>
    <p>{`Atlassian’s foundational styles and components are getting a refresh to improve usability and
showcase our new brand.`}</p>
    <p>{`Note that some features are still `}<strong parentName="p">{`early access`}</strong>{` (ready to preview and test, but not for
widespread use).`}</p>
    <img src={jiraScreenNewStyles} alt="A Jira screen showing all the new Atlassian UI styles used together." caption="A Jira screen using the new styles" borderAppearance="rounded-border" />
    <h3 {...{
      "id": "typography"
    }}>{`Typography`}</h3>
    <p>{`We’re rolling out more readable fonts with more distinct heading levels to aid scanning.`}</p>
    <p>{`Updated text components and tokens are ready to use in code and our Figma libraries.`}</p>
    <img src={typographyStyles} alt="A comparison of current and new typography styles" caption="The current text styles compared to Atlassian Sans and Atlassian Mono." />
    <h3 {...{
      "id": "iconography"
    }}>{`Iconography`}</h3>
    <p>{`Icons have a new, more streamlined style. In the new system, icon sizes and shapes are more
consistent and fit better with the refreshed text styles.`}</p>
    <p>{`New icons are available in code and Atlassian Figma libraries.`}</p>
    <img src={iconographyStyles} alt="A comparison of the current filled icon styles and the new line-based icon styles, showing the modernized and consistent design approach." />
    <h3 {...{
      "id": "color"
    }}>{`Color`}</h3>
    <p>{`New colors are being trialed in some apps to showcase our refreshed brand identity.`}</p>
    <p>{`We’ll update this page and the general color guidance when the new colors are ready for broad use.`}</p>
    <img src={colorStyles} alt="A comparison of current and new color styles, with noticeable differences between shades of orange, purple, and neutral colors." />
    <h3 {...{
      "id": "components"
    }}>{`Components`}</h3>
    <p>{`We’re testing some component changes to go with the new styles.`}</p>
    <p>{`You can preview some of these `}<strong parentName="p">{`early access`}</strong>{` updates in our component examples.`}</p>
    <img src={componentStyles} alt="A comparison of current and new color styles, showcasing noticeable differences in shades of orange, purple, and neutral tones." />
    <h2 {...{
      "id": "try-out-the-new-look"
    }}>{`Try out the new look`}</h2>
    <p>{`In `}<a parentName="p" {...{
        "href": "/components/lozenge/examples"
      }}>{`component examples`}</a>{`, choose `}<strong parentName="p">{`Styles`}</strong>{` then select the new design
language to see refreshed styles applied.`}</p>
    <img src={stylesDropdownLegacy} alt="A component example from the Atlassian Design System documentation, featuring the new Styles dropdown menu which allows users to preview and apply the updated design styles." borderAppearance="rounded-border" />
    <p>{`Select the `}<strong parentName="p">{`Custom`}</strong>{` option to choose individual styles. This is helpful if you want to see how the
component looks with specific styles turned on (controlled by
`}<a parentName="p" {...{
        "href": "https://go.atlassian.com/refresh-feature-flags"
      }}>{`feature flags`}</a>{`).`}</p>
    <p>{`For example, if you’re migrating to the new icons, you can customize the examples to just show new
icons.`}</p>
    <img src={stylesDropdownCustom} alt="The new Styles dropdown menu for component examples, which allows users to customize previews using combinations of Typography, Iconography, and Color and component changes" borderAppearance="rounded-border" />
    <h2 {...{
      "id": "use-the-new-styles"
    }}>{`Use the new styles`}</h2>
    <p>{`Some apps are already testing the refreshed styles with customers. Check out guidance on using the
latest styles and components:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/foundations/iconography"
        }}>{`Use updated iconography`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/foundations/typography"
        }}>{`Use new fonts and typography styles`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components"
        }}>{`Preview color and components changes in component examples`}</a></li>
    </ul>
    <h3 {...{
      "id": "for-atlassians"
    }}>{`For Atlassians`}</h3>
    <p>{`Get more details on using refreshed UI in Figma and in code:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/get-started/design/figma-libraries/atlassians"
        }}>{`Design with the latest Figma libraries using the new design language`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://go.atlassian.com/refresh-feature-flags"
        }}>{`Migrate code and use refreshed components with feature flags`}</a></li>
    </ul>
    <h3 {...{
      "id": "for-marketplace-partners"
    }}>{`For Marketplace partners`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/get-started/design/figma-libraries/marketplace-partners"
        }}>{`Use the latest community Figma library`}</a></li>
    </ul>
    <p>{`We’ll update this page and the developer community with more resources soon.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      